import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import axios from 'axios';
import LoadingSpinner from '../../../components/common/LoadingSpinner';

const TourCategories = () => {
  const [imageErrors, setImageErrors] = useState({});
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  
  // Fetch categories from the backend
  const { data: categoriesData, isLoading, error } = useQuery('tourCategories', async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/v1/categories?type=tour`);
      console.log('Categories API response:', response.data);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  }, {
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  // Default fallback images for different category types
  const getFallbackImageByType = (categoryName) => {
    if (!categoryName) return 'https://images.unsplash.com/photo-1528733918455-5a59687cedf0?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80';
    
    const name = categoryName.toLowerCase();
    if (name.includes('beach') || name.includes('sea')) {
      return 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80';
    } else if (name.includes('safari') || name.includes('wildlife')) {
      return 'https://images.unsplash.com/photo-1516426122078-c23e76319801?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80';
    } else if (name.includes('mountain') || name.includes('hiking')) {
      return 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80';
    } else if (name.includes('cultural') || name.includes('heritage')) {
      return 'https://images.unsplash.com/photo-1533669955142-6a2017de3c07?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80';
    } else if (name.includes('adventure')) {
      return 'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80';
    } else if (name.includes('luxury')) {
      return 'https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80';
    }
    
    return 'https://images.unsplash.com/photo-1528733918455-5a59687cedf0?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80';
  };

  const handleImageError = (categoryId) => {
    setImageErrors(prev => ({
      ...prev,
      [categoryId]: true
    }));
  };

  // Get image URL with fallback
  const getImageUrl = (category) => {
    if (imageErrors[category.id]) {
      return getFallbackImageByType(category.name);
    }
    
    // Check for image_url first (this comes from our backend transformation)
    if (category.image_url) {
      return category.image_url;
    }
    
    // Check for image in various possible properties
    const imageField = category.image || category.featured_image || category.thumbnail || category.cover_image;
    
    if (imageField) {
      // If image is a full URL
      if (imageField.startsWith('http://') || imageField.startsWith('https://')) {
        return imageField;
      }
      
      // If image is a relative path
      return `${apiUrl}/storage/${imageField}`;
    }
    
    // Check if there's an images array
    if (category.images && Array.isArray(category.images) && category.images.length > 0) {
      const firstImage = category.images[0];
      if (typeof firstImage === 'string') {
        if (firstImage.startsWith('http://') || firstImage.startsWith('https://')) {
          return firstImage;
        }
        return `${apiUrl}/storage/${firstImage}`;
      } else if (firstImage.url) {
        return firstImage.url.startsWith('http') ? firstImage.url : `${apiUrl}/storage/${firstImage.url}`;
      }
    }
    
    // Use the fallback image based on category name
    return getFallbackImageByType(category.name);
  };

  // Get tour count for a category
  const getTourCount = (category) => {
    return category.tour_count || category.count || 0;
  };

  // Category card component
  const CategoryCard = ({ category }) => {
    return (
      <Link 
        to={`/tours/category/${category.id || category.slug}`} 
        key={category.id} 
        className="category-card group relative overflow-hidden rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
        style={{ height: '250px' }}
      >
        {/* Category Image */}
        <div className="absolute inset-0 bg-gray-200">
          <img 
            src={getImageUrl(category)} 
            alt={category.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            onError={() => handleImageError(category.id)}
            loading="eager"
          />
          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
        </div>
        
        {/* Category Info */}
        <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
          <h3 className="text-xl font-bold mb-1 group-hover:text-primary-light transition-colors">
            {category.name}
          </h3>
          <p className="text-sm text-white/80 mb-2">
            {category.description}
          </p>
          <div className="flex items-center text-sm">
            <span className="bg-primary/80 px-2 py-1 rounded text-xs">
              {getTourCount(category)} tours
            </span>
          </div>
        </div>
      </Link>
    );
  };

  return (
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-2">Explore Tour Categories</h2>
          <p className="text-gray-600">
            Discover the diverse travel experiences available, from wildlife safaris to beach getaways
          </p>
        </div>
        
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-red-500 mb-4">Unable to load categories at this time.</p>
            <p className="text-gray-600">Please try again later or contact support if the problem persists.</p>
          </div>
        ) : !categoriesData || categoriesData.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-600 mb-4">No tour categories found.</p>
            <p className="text-sm text-gray-500">Categories can be added from the admin dashboard.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categoriesData.slice(0, 6).map((category) => (
              <CategoryCard key={category.id} category={category} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default TourCategories;
